import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ArrowRight, Check, Mail, Phone, MapPin } from "lucide-react";
import { WhatsAppIcon } from "@/components/PageCTA";
import { HERO_KONTAKT } from "@/components/heroBg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  { id: "web", label: "Strona WWW" },
  { id: "google", label: "Wizytówka Google" },
  { id: "graphics", label: "Grafiki" },
  { id: "logo", label: "Logo" },
  { id: "materials", label: "Materiały reklamowe" },
  { id: "cooperation", label: "Stała współpraca" },
];

const Kontakt = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", message: "", services: [], consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const formRef = useRef(null);
  const isInView = useInView(formRef, { once: true });

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message || !formData.consent) {
      toast.error("Wypełnij wymagane pola");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      toast.success("Wiadomość wysłana!");
    } catch (error) {
      toast.error("Wystąpił błąd");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="kontakt-page">
      {/* Hero */}
      <div className="subpage-hero subpage-hero--bg flex items-end" style={HERO_KONTAKT}>
        <div className="container pb-0">
          <p className="eyebrow mb-4">Kontakt</p>
          <h1 className="font-heading text-[clamp(36px,5vw,56px)] text-[var(--color-text-light)] mb-4">
            Zacznijmy Twój projekt
          </h1>
          <p className="lead max-w-lg">
            Napisz, czego potrzebujesz. Dobierzemy odpowiedni zakres i przygotujemy indywidualną wycenę.
          </p>
        </div>
      </div>

      {/* Form + Sidebar */}
      <div className="section bg-[var(--color-cream)]">
        <div className="container">
          <div className="grid lg:grid-cols-[1fr,340px] gap-12">
            {/* Form */}
            <motion.div
              ref={formRef}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              {success ? (
                <div className="text-center py-20 bg-[var(--color-white)] rounded border border-[var(--color-border-light)]">
                  <div className="w-16 h-16 mx-auto mb-6 bg-[var(--color-gold)]/20 rounded-full flex items-center justify-center">
                    <Check className="w-8 h-8 text-[var(--color-gold)]" />
                  </div>
                  <h2 className="font-heading text-2xl text-[var(--color-text-dark)] mb-3">Wiadomość wysłana!</h2>
                  <p className="font-body text-[var(--color-text-muted)] mb-8">Odpowiemy najszybciej jak to możliwe.</p>
                  <button onClick={() => setSuccess(false)} className="button-outline text-[var(--color-text-dark)]">Wyślij kolejną</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-body font-semibold text-[var(--color-text-muted)] mb-2 block">Imię *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-[var(--color-white)] border-[var(--color-border-light)] rounded-sm text-[var(--color-text-dark)] h-12 focus:border-[var(--color-gold)] focus-visible:ring-0 font-body"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-body font-semibold text-[var(--color-text-muted)] mb-2 block">E-mail *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="bg-[var(--color-white)] border-[var(--color-border-light)] rounded-sm text-[var(--color-text-dark)] h-12 focus:border-[var(--color-gold)] focus-visible:ring-0 font-body"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-body font-semibold text-[var(--color-text-muted)] mb-2 block">Telefon</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="bg-[var(--color-white)] border-[var(--color-border-light)] rounded-sm text-[var(--color-text-dark)] h-12 focus:border-[var(--color-gold)] focus-visible:ring-0 font-body"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-body font-semibold text-[var(--color-text-muted)] mb-2 block">Firma / Hodowla</Label>
                      <Input
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="bg-[var(--color-white)] border-[var(--color-border-light)] rounded-sm text-[var(--color-text-dark)] h-12 focus:border-[var(--color-gold)] focus-visible:ring-0 font-body"
                        data-testid="contact-company"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider font-body font-semibold text-[var(--color-text-muted)] mb-3 block">Czym jesteś zainteresowany/a?</Label>
                    <div className="flex flex-wrap gap-2">
                      {serviceOptions.map((service) => (
                        <button
                          key={service.id}
                          type="button"
                          onClick={() => handleServiceToggle(service.id)}
                          className={`px-4 py-2 text-xs font-body font-semibold rounded-sm border uppercase tracking-wider transition-all ${
                            formData.services.includes(service.id)
                              ? "border-[var(--color-gold)] text-[var(--color-gold)] bg-[var(--color-gold)]/10"
                              : "border-[var(--color-border-light)] text-[var(--color-text-muted)] hover:border-[var(--color-gold)]"
                          }`}
                        >
                          {service.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider font-body font-semibold text-[var(--color-text-muted)] mb-2 block">Wiadomość *</Label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={5}
                      className="bg-[var(--color-white)] border-[var(--color-border-light)] rounded-sm text-[var(--color-text-dark)] focus:border-[var(--color-gold)] focus-visible:ring-0 resize-none font-body"
                      data-testid="contact-message"
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={formData.consent}
                      onCheckedChange={(checked) => setFormData({ ...formData, consent: checked })}
                      className="mt-0.5 border-[var(--color-border-light)] data-[state=checked]:bg-[var(--color-gold)] data-[state=checked]:border-[var(--color-gold)]"
                      data-testid="contact-consent"
                    />
                    <span className="text-xs font-body text-[var(--color-text-muted)]">
                      Wyrażam zgodę na przetwarzanie danych osobowych *
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button type="submit" disabled={loading} className="button-primary" data-testid="contact-submit">
                      {loading ? "Wysyłanie..." : "Wyślij wiadomość"} <ArrowRight className="w-4 h-4" />
                    </button>
                    <a href="https://wa.me/48507334540" target="_blank" rel="noopener noreferrer">
                      <button type="button" className="button-outline flex items-center gap-2 text-[var(--color-text-dark)]" data-testid="contact-whatsapp-form">
                        <WhatsAppIcon className="w-4 h-4" /> Wyślij na WhatsApp
                      </button>
                    </a>
                  </div>
                </form>
              )}
            </motion.div>

            {/* Sidebar */}
            <div className="space-y-5">
              {/* Contact info card */}
              <div className="bg-[var(--color-white)] border border-[var(--color-border-light)] rounded p-6">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded bg-[var(--color-gold)]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[var(--color-gold)]" strokeWidth={1.4} />
                    </div>
                    <div>
                      <h3 className="text-xs uppercase tracking-wider font-body font-semibold text-[var(--color-text-muted)] mb-1">E-mail</h3>
                      <a href="mailto:aml01@vp.pl" className="text-sm font-body font-semibold text-[var(--color-text-dark)] hover:text-[var(--color-gold)] transition-colors">aml01@vp.pl</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded bg-[var(--color-gold)]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-[var(--color-gold)]" strokeWidth={1.4} />
                    </div>
                    <div>
                      <h3 className="text-xs uppercase tracking-wider font-body font-semibold text-[var(--color-text-muted)] mb-1">Telefon</h3>
                      <a href="tel:+48507334540" className="text-sm font-body font-semibold text-[var(--color-text-dark)] hover:text-[var(--color-gold)] transition-colors">+48 507 334 540</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded bg-[var(--color-gold)]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[var(--color-gold)]" strokeWidth={1.4} />
                    </div>
                    <div>
                      <h3 className="text-xs uppercase tracking-wider font-body font-semibold text-[var(--color-text-muted)] mb-1">Kontakt</h3>
                      <p className="text-sm font-body font-semibold text-[var(--color-text-dark)]">Ania — ApisDesign</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp card */}
              <div className="bg-[var(--color-white)] border border-[var(--color-border-light)] rounded p-6">
                <h3 className="font-heading text-lg text-[var(--color-text-dark)] mb-2">Napisz na WhatsApp</h3>
                <p className="text-xs font-body text-[var(--color-text-muted)] leading-relaxed mb-4">
                  Szybki kontakt — napisz bezpośrednio na WhatsApp.
                </p>
                <a href="https://wa.me/48507334540" target="_blank" rel="noopener noreferrer" className="block">
                  <button className="button-primary w-full justify-center" data-testid="contact-whatsapp-btn">
                    <WhatsAppIcon className="w-4 h-4" /> WhatsApp
                  </button>
                </a>
              </div>

              {/* Consultation card */}
              <div className="bg-[var(--color-white)] border border-[var(--color-border-light)] rounded p-6">
                <img src="/images/ikona-konsultacja.png" alt="" className="w-12 h-12 mb-3" />
                <h3 className="font-heading text-lg text-[var(--color-text-dark)] mb-2">Umów bezpłatną konsultację</h3>
                <p className="text-xs font-body text-[var(--color-text-muted)] leading-relaxed">
                  Porozmawiajmy o Twojej marce, potrzebach i celach. Doradzimy najlepszy kierunek i dobierzemy rozwiązanie dopasowane do Twojego projektu.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Kontakt;
